import { Component, DestroyRef, effect, inject, OnDestroy, OnInit, untracked } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScreenActionsComponent } from '@app/shared/components/screen-actions/screen-actions.component';
import { ScreenActionsService } from '@app/services/screen-actions.service';
import { AppStore } from '@app/core/states';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GetDuePlannedExpensesUseCase, GetDuePlannedIncomesUseCase } from '@app/domain/use-cases';
import { DuePlannedExpensesDialogComponent } from '../../features/planned-expenses/due-planned-expenses-dialog.component';
import { DuePlannedIncomesDialogComponent } from '../../features/planned-incomes/due-planned-incomes-dialog.component';
import { openDialogWithRetry } from '@app/shared/utils/dialog';
import { DatabaseSyncService } from '../services/database-sync.service';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';

@Component({
  selector: 'app-authorized-layout',
  standalone: true,
  imports: [RouterOutlet, ScreenActionsComponent, NgTemplateOutlet, TranslateModule],
  template: `
    <app-screen-actions>
      <ng-container *ngTemplateOutlet="screenActionsService.actionsTemplate()" />
    </app-screen-actions>
    <div
      class="main-content flex flex-col flex-1 border-t border-surface-200 dark:border-surface-700"
      [class.follower-tab]="!appStore.isLeader()"
    >
      <router-outlet></router-outlet>
    </div>
    @if (databaseSyncService.isPersistencePending()) {
      <div
        class="save-indicator"
        role="status"
        aria-live="polite"
        [attr.aria-label]="'common.notifications.saving_changes' | translate"
        [attr.title]="'common.notifications.saving_changes' | translate"
      ></div>
    }
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100dvh; /* we should not use height here, it should be on html and body only */
    }

    .main-content ::ng-deep > *:not(router-outlet) {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .follower-tab {
      position: relative;
    }

    .follower-tab::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #f59e0b, #ef4444, #f59e0b);
      background-size: 200% 100%;
      animation: gradient-shift 3s ease infinite;
      z-index: 9999;
      pointer-events: none;
    }

    @keyframes gradient-shift {
      0%,
      100% {
        background-position: 0% 50%;
      }
      50% {
        background-position: 100% 50%;
      }
    }

    .save-indicator {
      position: fixed;
      top: calc(env(safe-area-inset-top, 0px) + 0.375rem);
      right: calc(env(safe-area-inset-right, 0px) + 0.375rem);
      z-index: 9998;
      width: 0.25rem;
      aspect-ratio: 1;
      border-radius: 9999px;
      background: var(--p-surface-800);
      pointer-events: none;
      animation:
        save-indicator-enter 160ms ease-out,
        save-indicator-pulse 4s ease-in-out infinite;
    }

    @keyframes save-indicator-enter {
      from {
        opacity: 0;
        transform: scale(0.7);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }

    @keyframes save-indicator-pulse {
      0%,
      100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 color-mix(in srgb, var(--p-surface-500) 45%, transparent);
      }
      50% {
        transform: scale(1.08);
        box-shadow: 0 0 0 0.45rem color-mix(in srgb, var(--p-surface-500) 0%, transparent);
      }
    }
  `,
})
export class AuthorizedLayoutComponent implements OnInit, OnDestroy {
  protected readonly screenActionsService = inject(ScreenActionsService);
  protected readonly appStore = inject(AppStore);
  protected readonly databaseSyncService = inject(DatabaseSyncService);

  private readonly dialogService = inject(DialogService);
  private readonly getDuePlannedExpenses = inject(GetDuePlannedExpensesUseCase);
  private readonly getDuePlannedIncomes = inject(GetDuePlannedIncomesUseCase);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  /** Prevent showing the dialog more than once per login session */
  private hasShownDueDialog = false;
  private resumeListenerHandle?: PluginListenerHandle;

  constructor() {
    effect(() => {
      const authorized = this.appStore.isAuthorized();
      untracked(() => {
        if (!authorized) {
          this.hasShownDueDialog = false;
          return;
        }
        if (this.hasShownDueDialog) return;
        this.hasShownDueDialog = true;
        this.checkAndShowDueExpenses();
      });
    });
  }

  ngOnInit() {
    this.setupBackgroundResumeCheck();
  }

  private checkAndShowDueExpenses(): void {
    // Fetch both lists in parallel, then show dialogs sequentially so they never overlap.
    Promise.allSettled([this.getDuePlannedExpenses.execute(), this.getDuePlannedIncomes.execute()]).then(
      ([expensesResult, incomesResult]) => {
        const expenses = expensesResult.status === 'fulfilled' ? expensesResult.value : [];
        const incomes = incomesResult.status === 'fulfilled' ? incomesResult.value : [];

        const openIncomesIfNeeded = () => {
          if (incomes.length === 0) return;
          openDialogWithRetry(this.dialogService, DuePlannedIncomesDialogComponent, {
            header: this.translate.instant('screens.planned_incomes.titles.due_incomes'),
            data: { items: incomes },
            style: { height: '90dvh' },
            contentStyle: { overflow: 'hidden', display: 'flex', flexDirection: 'column' },
          })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        };

        if (expenses.length === 0) {
          openIncomesIfNeeded();
          return;
        }

        // Show the expenses dialog first; open incomes dialog only after it closes.
        openDialogWithRetry(this.dialogService, DuePlannedExpensesDialogComponent, {
          header: this.translate.instant('screens.planned_expenses.titles.due_expenses'),
          data: { items: expenses },
          style: { height: '90dvh' },
          contentStyle: { overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({ complete: openIncomesIfNeeded });
      },
    );
  }

  private async setupBackgroundResumeCheck() {
    // Listen for the native Android resume event
    this.resumeListenerHandle = await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.checkSessionSanity();
      }
    });
  }

  private checkSessionSanity() {
    const isAuthorized = this.appStore.isAuthorized();

    // If the data layer says we are locked, but we are still looking at an old page:
    if (!isAuthorized) {
      console.warn('Session is locked upon resume. Forcing hard redirect to login.');

      // Bypasses the Angular router entirely to clear out stale component memory
      window.location.assign('/login');
    }
  }

  ngOnDestroy() {
    // Crucial: Clean up the native listener so it doesn't leak in the background
    if (this.resumeListenerHandle) {
      this.resumeListenerHandle.remove();
    }
  }
}
